/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Box, Flex, Grid, Heading, Text, Badge, Spinner, Progress, useColorModeValue
} from '@chakra-ui/react';
import { FaChartLine, FaBrain, FaBookOpen, FaShieldAlt, FaRobot } from 'react-icons/fa';
import axios from 'axios';

const scoreColor = (score) => {
  if (score >= 80) return 'green';
  if (score >= 50) return 'yellow';
  return 'red';
};

const scoreLabel = (score) => {
  if (score >= 80) return 'Excellent';
  if (score >= 50) return 'Good';
  return 'Needs Improvement';
};

const MyInsights = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState('');
  const [summaryLoading, setSummaryLoading] = useState(true);
  const token = JSON.parse(localStorage.getItem('user'))?.token || '';

  const cardBg = useColorModeValue('white', 'var(--bg-surface, #161428)');
  const textDark = useColorModeValue('#0a2628', 'var(--text-main, #EDE9FF)');
  const textMuted = useColorModeValue('#7a9c9f', 'var(--text-muted, #9B98BC)');
  const borderColor = useColorModeValue('gray.100', 'rgba(255,255,255,0.08)');
  const summaryBg = useColorModeValue('purple.50', 'rgba(157,148,248,0.1)');
  const summaryText = useColorModeValue('purple.800', 'var(--text-main, #EDE9FF)');

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/insights/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setIsLoading(false));

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/insights/me/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => setSummary(res.data.summary))
      .catch(console.error)
      .finally(() => setSummaryLoading(false));
  }, []);

  if (isLoading) return <Flex justify="center" p={10}><Spinner size="xl" /></Flex>;
  if (!data) return <Flex justify="center" p={10}><Text color="red.400">Failed to load insights. Please try again later.</Text></Flex>;

  const pillars = [
    {
      label: 'Quiz Performance',
      score: data.quizScore,
      icon: FaBrain,
      color: 'purple',
      detail: data.quizzesTaken > 0
        ? `${data.quizzesTaken} quiz${data.quizzesTaken !== 1 ? 'zes' : ''} taken`
        : 'No quizzes taken yet',
      weight: '30%'
    },
    {
      label: 'Course Completion',
      score: data.completionScore,
      icon: FaBookOpen,
      color: 'blue',
      detail: `${data.completedCourses} of ${data.totalEnrollments} courses completed`,
      weight: '40%'
    },
    {
      label: 'Compliance',
      score: data.complianceScore,
      icon: FaShieldAlt,
      color: 'teal',
      detail: data.complianceCourses > 0
        ? `${data.onTimeCompliance} of ${data.complianceCourses} completed on time`
        : 'No compliance courses assigned',
      weight: '30%'
    }
  ];

  return (
    <Box p={6}>
      <Flex align="center" gap={3} mb={8}>
        <Box p={3} bg="purple.50" borderRadius="2xl" color="purple.600">
          <FaChartLine size={24} />
        </Box>
        <Box>
          <Heading size="lg" fontWeight="900" color={textDark}>My Insights</Heading>
          <Text fontSize="sm" color={textMuted} fontWeight="600">
            Your learning performance across quizzes, courses, and compliance.
          </Text>
        </Box>
      </Flex>

      {/* AI Summary */}
      <Box bg={summaryBg} borderRadius="2xl" p={5} mb={6}>
        <Flex align="center" gap={2} mb={3}>
          <FaRobot color="#7c3aed" size={14} />
          <Text fontWeight="800" fontSize="xs" color="purple.600" letterSpacing="1px">AI PERFORMANCE SUMMARY</Text>
        </Flex>
        {summaryLoading ? (
          <Flex align="center" gap={3}>
            <Spinner size="sm" color="purple.500" />
            <Text fontSize="sm" color={textMuted}>Generating your summary...</Text>
          </Flex>
        ) : (
          <Text fontSize="sm" color={summaryText} lineHeight={1.8} fontWeight="500">{summary}</Text>
        )}
      </Box>

      {/* Overall Score */}
      <Box
        bg={cardBg} border="1px solid" borderColor={borderColor}
        borderRadius="3xl" p={8} mb={6} textAlign="center" boxShadow="sm"
      >
        <Text fontSize="xs" fontWeight="800" color={textMuted} letterSpacing="1px" mb={2}>
          OVERALL SCORE
        </Text>
        <Text fontSize="7xl" fontWeight="900" color={`${scoreColor(data.overallScore)}.500`} lineHeight={1}>
          {data.overallScore}%
        </Text>
        <Badge mt={3} colorScheme={scoreColor(data.overallScore)} borderRadius="full" px={4} py={1} fontSize="sm">
          {scoreLabel(data.overallScore)}
        </Badge>
        <Box mt={5} maxW="320px" mx="auto">
          <Progress
            value={data.overallScore}
            colorScheme={scoreColor(data.overallScore)}
            borderRadius="full"
            size="lg"
          />
        </Box>
        <Text fontSize="xs" color={textMuted} mt={3} fontWeight="600">
          Quiz (30%) + Course Completion (40%) + Compliance (30%)
        </Text>
      </Box>

      {/* Three Pillars */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={5}>
        {pillars.map(({ label, score, icon: Icon, color, detail, weight }) => (
          <Box
            key={label}
            bg={cardBg} border="1px solid" borderColor={borderColor}
            borderRadius="2xl" p={6} boxShadow="sm"
          >
            <Flex align="center" justify="space-between" mb={4}>
              <Flex align="center" gap={3}>
                <Box p={2} bg={`${color}.50`} borderRadius="xl" color={`${color}.500`}>
                  <Icon size={16} />
                </Box>
                <Text fontWeight="800" fontSize="sm" color={textDark}>{label}</Text>
              </Flex>
              <Badge colorScheme="gray" fontSize="9px" borderRadius="full">{weight}</Badge>
            </Flex>
            <Text fontSize="4xl" fontWeight="900" color={`${scoreColor(score)}.500`} mb={1}>
              {score}%
            </Text>
            <Text fontSize="xs" color={textMuted} mb={3}>{detail}</Text>
            <Progress value={score} colorScheme={scoreColor(score)} borderRadius="full" size="sm" />
          </Box>
        ))}
      </Grid>
    </Box>
  );
};

export default MyInsights;